import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import BottomDrawer from '../Drawer/Drawer';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import SectionLoader from '../Loader/SectionLoader';
import SelectItem from './components/SelectItem';
import paymentTypes, {
  PAYMENT_METHOD_TYPES,
  PAYMENT_METHOD_TYPES_ICONS,
} from '../../util/enum/api/paymentTypes';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import AddPaymentMethod from './AddPaymentMethod';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';

const PaymentOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
`;

const AddPaymentMethodButton = styled.button`
  width: 100%;
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px dashed ${themeColors.colorGrey3};
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  background-color: transparent;
  padding: ${pxToRem(10)};
  cursor: pointer;
`;

const MaskedContainer = styled.div`
  font-size: 12px;
  color: ${themeColors.colorTextSecondary};
  display: flex;
  align-self: flex-start;

  span {
    font-size: 12px;
  }
`;

const PaymentMethodLimit = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 12px;
  }
`;

const SelectPaymentOptions = ({
  open,
  setOpen,
  paymentMethods,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setIsAddAddressOpen,
  isAddAddressOpen,
  accountUid,
  // setIsAddPaymentMethodOpen,
}) => {
  const { t } = useTranslation();
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const dispatch = useDispatch();
  const [isNewCardAdded, setIsNewCardAdded] = useState(false);
  const [isNewACHAdded, setIsNewACHAdded] = useState(false);

  useEffect(() => {
    if (isNewCardAdded || isNewACHAdded) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
  }, [accountUid, dispatch, isNewCardAdded, isNewACHAdded]);

  const paymentMethodInfo = (method) => {
    if (method?.AccountBalance || method?.AccountBalance === 0) {
      return (
        <MaskedContainer>
          {formatMoneyNumeral(method?.AccountBalance)}
        </MaskedContainer>
      );
    }
    if (method?.BankName) {
      return (
        <>
          <MaskedContainer>
            <span>***</span>
            <span>{method?.BankAccountMask}</span>
          </MaskedContainer>
          <PaymentMethodLimit>
            {t('buyWizard.payment.limitPerDay', {
              limit: method?.MaximumLimit,
            })}
          </PaymentMethodLimit>
        </>
      );
    }
    if (method?.CreditCardType) {
      return (
        <>
          <MaskedContainer>
            <span>***</span>
            <span>{method?.Last4Digits}</span>
          </MaskedContainer>
          <PaymentMethodLimit>
            {t('buyWizard.payment.limitPerOrder', {
              limit: method?.MaximumLimit,
            })}
          </PaymentMethodLimit>
        </>
      );
    }
  };

  if (isAddPaymentMethodOpen) {
    return (
      <AddPaymentMethod
        isOpen={isAddPaymentMethodOpen}
        onClose={() => setIsAddPaymentMethodOpen(false)}
        avoidClearingStorage
        setIsAddAddressOpen={setIsAddAddressOpen}
        isAddAddressOpen={isAddAddressOpen}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        setIsNewCardAdded={setIsNewCardAdded}
        setIsNewACHAdded={setIsNewACHAdded}
      />
    );
  }

  return (
    <BottomDrawer
      isOpen={open}
      onClose={() => setOpen(false)}
      title={t('buyWizard.payment.selectPaymentOptions')}
    >
      <SectionLoader
      // isLoading={isLoading}
      >
        <PaymentOptionsWrapper>
          <AddPaymentMethodButton
            onClick={() => {
              setIsAddPaymentMethodOpen(true);
            }}
          >
            {t('buyWizard.payment.addPaymentMethod')}
          </AddPaymentMethodButton>
          {paymentMethods?.map((paymentMethod) => (
            <SelectItem
              key={paymentMethod?.PaymentMethodUid}
              selectItemTitle={`${paymentMethod?.BankName ?? ''} ${t(
                `buyWizard.payment.paymentMethods.${
                  paymentTypes[paymentMethod?.PaymentMethodType]
                }`,
              )}`}
              selectItemSubtitle={paymentMethodInfo(paymentMethod)}
              icon={
                PAYMENT_METHOD_TYPES_ICONS[paymentMethod?.PaymentMethodType]
              }
              selectItemRibbon={
                paymentMethod?.PaymentMethodType !==
                  PAYMENT_METHOD_TYPES.CREDIT_CARD &&
                t('buyWizard.payment.savePrecent')
              }
              backgroundColor={themeColors.colorSuccess}
              isSelected={
                selectedPaymentMethod?.PaymentMethodUid ===
                paymentMethod?.PaymentMethodUid
              }
              onClick={() => setSelectedPaymentMethod(paymentMethod)}
              // TO DO: Change below isDisabled to use isPaymentMethodDisabled when getQuote ias added
              isDisabled={
                paymentMethod?.PaymentMethodType === PAYMENT_METHOD_TYPES.ACH &&
                paymentMethod?.BankAccountSetupType === 2
              }
              // isDisabled={isPaymentMethodDisabled(
              //   paymentMethod,
              //   totalAmountWithCreditCard,
              //   totalAmountWithoutCreditCard,
              //   isAccountBalanceDisabled,
              // )}
            />
          ))}
        </PaymentOptionsWrapper>
      </SectionLoader>
    </BottomDrawer>
  );
};

SelectPaymentOptions.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  wizardData: PropTypes.shape({}),
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({})),
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodUid: PropTypes.number,
  }),
  setSelectedPaymentMethod: PropTypes.func,
  setIsAddAddressOpen: PropTypes.func,
  isAddAddressOpen: PropTypes.bool,
  accountUid: PropTypes.string,
};

export default SelectPaymentOptions;
